import { useState, useRef } from 'react';
import { FileText, Download, Loader2, CheckCircle2, AlertCircle, Calendar, Hash, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { useEmployees } from '../hooks/useEmployees';
import {
  useRequestDocument,
  useAllDocumentsAdmin,
  useUploadDocument,
  type DocumentType,
  type RequestDocumentResponse,
} from '../hooks/documents/useDocuments';
import { apiClient } from '../lib/api';
import { APP_TIME_ZONE } from '@/utils/dateFormat';

export function Documents() {
  // Form state (1C request)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [documentType, setDocumentType] = useState<DocumentType>('PAYSLIP');
  const [period, setPeriod] = useState<string>('');
  const [documentId, setDocumentId] = useState<string>('');

  // Result state (1C request)
  const [requestResult, setRequestResult] = useState<RequestDocumentResponse | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Upload form state
  const [uploadTitle, setUploadTitle] = useState<string>('');
  const [uploadVisibility, setUploadVisibility] = useState<'ALL' | 'MANAGERS_ONLY'>('ALL');
  const [uploadDocType, setUploadDocType] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hooks
  const { data: employeesData, isLoading: isLoadingEmployees } = useEmployees({ limit: 10000, isActive: true });
  const requestDocument = useRequestDocument();
  const uploadDocument = useUploadDocument();
  const { data: allDocuments, isLoading: isLoadingDocuments } = useAllDocumentsAdmin({ limit: 10 });

  // Reset 1C request form
  const handleReset = () => {
    setRequestResult(null);
    setRequestError(null);
    setSelectedEmployeeId('');
    setPeriod('');
    setDocumentId('');
  };

  // Reset upload form
  const handleUploadReset = () => {
    setUploadTitle('');
    setUploadVisibility('ALL');
    setUploadDocType('');
    setUploadFile(null);
    setUploadSuccess(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Request document from 1C
  const handleRequestDocument = async () => {
    if (!selectedEmployeeId) {
      setRequestError('Выберите сотрудника');
      return;
    }

    if (documentType === 'PAYSLIP' && !period) {
      setRequestError('Укажите период (ГГГГ-ММ) для расчётного листка');
      return;
    }

    if ((documentType === 'ORDER' || documentType === 'CERTIFICATE') && !documentId) {
      setRequestError('Укажите ID документа');
      return;
    }

    setRequestError(null);
    setRequestResult(null);

    try {
      const result = await requestDocument.mutateAsync({
        employeeId: selectedEmployeeId,
        documentType,
        period: documentType === 'PAYSLIP' ? period : undefined,
        documentId: documentType === 'ORDER' || documentType === 'CERTIFICATE' ? documentId : undefined,
      });

      setRequestResult(result);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Не удалось получить документ из 1С';
      setRequestError(errorMessage);
    }
  };

  // Upload document
  const handleUploadDocument = async () => {
    if (!uploadTitle.trim()) {
      setUploadError('Введите название документа');
      return;
    }
    if (!uploadFile) {
      setUploadError('Выберите файл');
      return;
    }

    setUploadError(null);
    setUploadSuccess(null);

    try {
      await uploadDocument.mutateAsync({
        title: uploadTitle.trim(),
        visibility: uploadVisibility,
        documentType: uploadDocType || undefined,
        file: uploadFile,
      });

      const visLabel = uploadVisibility === 'ALL' ? 'всем сотрудникам' : 'только управляющим';
      setUploadSuccess(`Документ "${uploadTitle}" загружен (${visLabel})`);
      setUploadTitle('');
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Не удалось загрузить документ';
      setUploadError(errorMessage);
    }
  };

  // Download document (Admin)
  const handleDownload = async (docId: string) => {
    try {
      const { url } = await apiClient.downloadDocumentAdmin(docId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Не удалось скачать документ';
      alert(`Ошибка скачивания: ${errorMessage}`);
    }
  };

  // Get selected employee name (1C form)
  const selectedEmployee = employeesData?.data.find((e) => e.id === selectedEmployeeId);
  const selectedEmployeeName = selectedEmployee
    ? `${selectedEmployee.last_name} ${selectedEmployee.first_name} ${selectedEmployee.middle_name || ''}`.trim()
    : '';

  // Document type labels
  const documentTypeLabels: Record<DocumentType, string> = {
    PAYSLIP: 'Расчётный листок',
    ORDER: 'Приказ',
    CERTIFICATE: 'Справка',
    CONTRACT: 'Трудовой договор',
  };

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Breadcrumbs */}
      <Breadcrumbs />

      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileText className="h-8 w-8" />
          Документы
        </h1>
        <p className="text-muted-foreground mt-2">
          Загрузка документов для сотрудников и запрос из 1С
        </p>
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Загрузить документ
          </CardTitle>
          <CardDescription>
            Загрузите файл для сотрудников. Они получат уведомление и увидят документ в приложении.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Visibility Toggle */}
          <div className="space-y-2">
            <Label>Видимость</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={uploadVisibility === 'ALL' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setUploadVisibility('ALL')}
              >
                Видно всем
              </Button>
              <Button
                type="button"
                variant={uploadVisibility === 'MANAGERS_ONLY' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setUploadVisibility('MANAGERS_ONLY')}
              >
                Только управляющим
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {uploadVisibility === 'ALL'
                ? 'Документ будет виден всем сотрудникам в приложении'
                : 'Документ будет виден только руководителям отделов'}
            </p>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="upload-title">Название документа</Label>
            <Input
              id="upload-title"
              type="text"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="Например: Справка о доходах за 2024 год"
            />
          </div>

          {/* Document Type (optional) */}
          <div className="space-y-2">
            <Label htmlFor="upload-doc-type">Тип документа (необязательно)</Label>
            <Select value={uploadDocType} onValueChange={setUploadDocType}>
              <SelectTrigger id="upload-doc-type">
                <SelectValue placeholder="Выберите тип..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAYSLIP">Расчётный листок</SelectItem>
                <SelectItem value="ORDER">Приказ</SelectItem>
                <SelectItem value="CERTIFICATE">Справка</SelectItem>
                <SelectItem value="CONTRACT">Трудовой договор</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* File Input */}
          <div className="space-y-2">
            <Label htmlFor="upload-file">Файл</Label>
            <Input
              id="upload-file"
              type="file"
              ref={fileInputRef}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
            <p className="text-xs text-muted-foreground">
              PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (до 10 МБ)
            </p>
          </div>

          {/* Upload Error */}
          {uploadError && (
            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900">Ошибка</h3>
                  <p className="text-sm text-red-700 mt-1">{uploadError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Upload Success */}
          {uploadSuccess && (
            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900">Документ загружен!</h3>
                  <p className="text-sm text-green-700 mt-1">{uploadSuccess}</p>
                </div>
              </div>
            </div>
          )}

          {/* Upload Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleUploadDocument}
              disabled={uploadDocument.isPending || !uploadTitle.trim() || !uploadFile}
              className="gap-2"
            >
              {uploadDocument.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {uploadDocument.isPending ? 'Загрузка...' : 'Загрузить'}
            </Button>
            <Button variant="outline" onClick={handleUploadReset}>
              Сбросить
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 1C Request Form */}
      <Card>
        <CardHeader>
          <CardTitle>Запросить документ из 1С</CardTitle>
          <CardDescription>
            Выберите сотрудника и тип документа. Документ будет получен из 1С или вернётся из кэша.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Employee Select */}
          <div className="space-y-2">
            <Label htmlFor="employee">Сотрудник</Label>
            <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
              <SelectTrigger id="employee" disabled={isLoadingEmployees}>
                <SelectValue placeholder="Выберите сотрудника..." />
              </SelectTrigger>
              <SelectContent>
                {employeesData?.data.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {`${employee.last_name} ${employee.first_name} ${employee.middle_name || ''}`.trim()} (#{employee.employee_number})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEmployee && !selectedEmployee.code_1c && (
              <p className="text-sm text-destructive">
                У сотрудника нет code_1c - генерация документов невозможна
              </p>
            )}
          </div>

          {/* Document Type Select */}
          <div className="space-y-2">
            <Label htmlFor="document-type">Тип документа</Label>
            <Select
              value={documentType}
              onValueChange={(value) => setDocumentType(value as DocumentType)}
            >
              <SelectTrigger id="document-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PAYSLIP">Расчётный листок</SelectItem>
                <SelectItem value="ORDER">Приказ</SelectItem>
                <SelectItem value="CERTIFICATE">Справка</SelectItem>
                <SelectItem value="CONTRACT">Трудовой договор</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional fields */}
          {documentType === 'PAYSLIP' && (
            <div className="space-y-2">
              <Label htmlFor="period" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Период (ГГГГ-ММ)
              </Label>
              <Input
                id="period"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value.replace('-', '-'))}
                placeholder="2025-05"
              />
              <p className="text-xs text-muted-foreground">
                Формат: ГГГГ-ММ (например, 2025-05 для мая 2025)
              </p>
            </div>
          )}

          {(documentType === 'ORDER' || documentType === 'CERTIFICATE') && (
            <div className="space-y-2">
              <Label htmlFor="document-id" className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                ID документа в 1С
              </Label>
              <Input
                id="document-id"
                type="text"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                placeholder="ORDER-2024-001"
              />
              <p className="text-xs text-muted-foreground">
                {documentType === 'ORDER'
                  ? 'Номер приказа в системе 1С (например, ORDER-2024-001)'
                  : 'Номер справки в системе 1С (например, CERT-2024-067)'}
              </p>
            </div>
          )}

          {/* Error Message */}
          {requestError && (
            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900">Ошибка</h3>
                  <p className="text-sm text-red-700 mt-1">{requestError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {requestResult && (
            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900">Документ получен!</h3>
                  <div className="space-y-2 mt-2 text-sm text-green-800">
                    <p>
                      <strong>Сотрудник:</strong> {selectedEmployeeName}
                    </p>
                    <p>
                      <strong>Тип:</strong> {documentTypeLabels[documentType]}
                    </p>
                    {requestResult.fromCache && (
                      <p className="text-green-700">
                        Документ получен из кэша
                      </p>
                    )}
                    {!requestResult.fromCache && (
                      <p className="text-green-700">
                        Документ сгенерирован в 1С
                      </p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleDownload(requestResult.id)}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Скачать документ
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleReset}>
                        Запросить ещё
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleRequestDocument}
              disabled={requestDocument.isPending || !selectedEmployeeId}
              className="gap-2"
            >
              {requestDocument.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {requestDocument.isPending ? 'Запрос к 1С...' : 'Запросить документ'}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Сбросить
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Documents */}
      <Card>
        <CardHeader>
          <CardTitle>Последние документы</CardTitle>
          <CardDescription>Список последних документов</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingDocuments && (
            <div className="text-center py-4 text-muted-foreground">Загрузка...</div>
          )}

          {!isLoadingDocuments && allDocuments?.data && allDocuments.data.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>Документов пока нет</p>
            </div>
          )}

          {!isLoadingDocuments && allDocuments?.data && allDocuments.data.length > 0 && (
            <div className="space-y-2">
              {allDocuments.data.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium">{doc.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {doc.employee
                        ? `${doc.employee.last_name} ${doc.employee.first_name} ${doc.employee.middle_name || ''}`.trim()
                        : doc.metadata?.visibility === 'ALL'
                          ? 'Все сотрудники'
                          : doc.metadata?.visibility === 'MANAGERS_ONLY'
                            ? 'Только управляющие'
                            : ''}
                      {doc.metadata?.source === 'ADMIN_UPLOAD' && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          загружен
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(doc.createdAt).toLocaleString('ru-RU', { timeZone: APP_TIME_ZONE })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(doc.id)}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Скачать
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
